import React, { useContext, useRef, useState } from 'react';

import { Box, Chip, makeStyles, Theme, createStyles } from '@material-ui/core';

import { blobToBase64 } from '../../../../utils/blobToBase64';
import { AIChatContext } from '../../context';
import { AI_CHAT_FILE_ACCEPTS } from '../../constants';
import { ChatComposer, ChatComposerFiles, ChatComposerRef } from '../../../../components/Chat/kit';

const FILE_INPUT_ACCEPT = Object.values(AI_CHAT_FILE_ACCEPTS).flat().join(',');

interface Props {
  placeholder?: string;
  // Lets the user attach files for Laila to read.
  attachments?: boolean;
  // Questions offered once a file is attached and nothing is typed yet.
  fileSuggestions?: string[];
  autoFocus?: boolean;
}

// Laila's message box: the shared chat composer, with Stop while she answers.
export const LailaComposer: React.FC<Props> = ({
  placeholder = 'Ask Laila anything',
  attachments = true,
  fileSuggestions = [],
  autoFocus = true,
}) => {
  const classes = useStyles();
  const { draft, setDraft, send, stop, thinking, files, uploadFile, deleteFile, speechToText } =
    useContext(AIChatContext);
  const [uploading, setUploading] = useState<string[]>([]);
  const composer = useRef<ChatComposerRef>(null);

  const submit = (text: string) => {
    if (!thinking && text.trim()) {
      send(text);
      composer.current?.focus();
    }
  };

  const attach = async (picked: File[]) => {
    for (const file of picked) {
      setUploading(value => [...value, file.name]);
      await uploadFile(file);
      setUploading(value => value.filter(name => name !== file.name));
    }
  };

  return (
    <ChatComposer
      ref={composer}
      value={draft}
      onChange={setDraft}
      onSend={submit}
      busy={thinking}
      onStop={stop}
      stopLabel="Stop Laila"
      placeholder={placeholder}
      autoFocus={autoFocus}
      onAttach={attachments ? attach : undefined}
      accept={FILE_INPUT_ACCEPT}
      // Voice goes into the box rather than straight to Laila, so it can be checked first.
      onTranscribe={async blob => speechToText(await blobToBase64(blob))}
      micDeniedMessage="Laila needs microphone access to hear you. Allow it in your browser settings."
      unclearRecordingMessage="Laila couldn't make out that recording."
      top={
        <>
          <ChatComposerFiles
            files={[
              ...files.map(file => ({
                key: file.fileId,
                name: file.name,
                size: file.size,
                onRemove: () => deleteFile(file.name),
              })),
              ...uploading.map(name => ({ key: `uploading-${name}`, name, uploading: true })),
            ]}
          />
          {!!files.length && !draft.trim() && !!fileSuggestions.length && (
            <Box className={classes.suggestions}>
              {fileSuggestions.map(suggestion => (
                <Chip
                  key={suggestion}
                  size="small"
                  variant="outlined"
                  clickable
                  label={suggestion}
                  onClick={() => submit(suggestion)}
                  className={classes.suggestion}
                />
              ))}
            </Box>
          )}
        </>
      }
    />
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    suggestions: {
      display: 'flex',
      gap: 6,
      padding: '0 4px',
      overflowX: 'auto',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': { display: 'none' },
    },
    suggestion: { flex: 'none', color: theme.palette.primary.main, borderColor: theme.palette.divider },
  })
);
